import { Transaction } from '../types';
import { resolveTransactionUnitPrice } from './costBasis';
import { normalizeMoneyUnit } from './quoteNormalization';

export type HoldingAggregate = {
    instrumentId?: string;
    providerKey?: string;
    symbol: string;
    displaySymbol: string;
    name: string;
    exchange?: string;
    currency: string;
    costCurrency: string;
    totalBuysQty: number;
    totalBuysAmount: number;
    totalSellsQty: number;
    totalSellsAmount: number;
    dividendsTotal: number;
    lastTradeDate?: string;
    quantity: number;
    costBasis: number;
    costBasisNative: number;
    avgCost: number;
    avgCostNative: number;
    holdingId: string;
};

const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const normalizeCurrency = (value: string | undefined) => (value || 'USD').trim().toUpperCase();

const resolveCostCurrency = (tx: Transaction) =>
    normalizeCurrency(tx.costCurrency || tx.totalCurrency || tx.currency || tx.priceCurrency || tx.originalPriceCurrency);

const resolveQuoteCurrency = (tx: Transaction) => {
    const raw = normalizeCurrency(tx.originalPriceCurrency || tx.priceCurrency || tx.currency || tx.totalCurrency);
    return normalizeMoneyUnit({ value: 1, rawCurrency: raw, exchange: tx.exchange, symbol: tx.symbol }).currency;
};

const getHoldingKey = (tx: Transaction) => {
    const symbol = (tx.displaySymbol || tx.symbol || 'UNKNOWN').trim().toUpperCase();
    const exchange = (tx.exchange || 'NA').trim().toUpperCase() || 'NA';
    const baseKey = tx.instrumentId?.trim() || tx.dataProviderId?.trim() || `${symbol}_${exchange}`;
    const costCurrency = resolveCostCurrency(tx);
    return `${baseKey}__${costCurrency}`;
};

const resolveHoldingKey = (
    tx: Transaction,
    map: Map<string, Omit<HoldingAggregate, 'avgCost' | 'avgCostNative' | 'holdingId'>>
) => {
    const preferredKey = getHoldingKey(tx);
    if (map.has(preferredKey)) return preferredKey;

    const hasExplicitIdentity = Boolean(tx.instrumentId?.trim() || tx.dataProviderId?.trim() || tx.exchange?.trim());
    if (hasExplicitIdentity) return preferredKey;

    const symbol = (tx.symbol || tx.displaySymbol || 'UNKNOWN').trim().toUpperCase();
    const costCurrency = resolveCostCurrency(tx);
    const candidates = Array.from(map.entries()).filter(([, entry]) => entry.symbol === symbol && entry.costCurrency === costCurrency);

    return candidates.length === 1 ? candidates[0][0] : preferredKey;
};

const toIsoDateOnly = (value: string | undefined) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString().slice(0, 10);
};

export const aggregateHoldings = (transactions: Transaction[]): HoldingAggregate[] => {
    const map = new Map<string, Omit<HoldingAggregate, 'avgCost' | 'avgCostNative' | 'holdingId'>>();

    transactions.filter((tx) => !tx.deletedAt).forEach((tx) => {
        const holdingId = resolveHoldingKey(tx, map);
        const quantity = toNumber(tx.quantity || tx.shares);
        const amount = Math.abs(toNumber(tx.total ?? tx.amount));
        const nativeUnitPrice = resolveTransactionUnitPrice(tx);
        const nativeAmount = nativeUnitPrice > 0 && quantity > 0 ? nativeUnitPrice * Math.abs(quantity) : amount;
        const action = (tx.action || '').toUpperCase();
        const isSell = action === 'SELL';

        const entry = map.get(holdingId) || {
            instrumentId: tx.instrumentId,
            providerKey: tx.dataProviderId,
            symbol: (tx.symbol || tx.displaySymbol || 'UNKNOWN').trim().toUpperCase(),
            displaySymbol: (tx.displaySymbol || tx.symbol || 'UNKNOWN').trim().toUpperCase(),
            name: tx.name || tx.displaySymbol || tx.symbol || 'Unknown',
            exchange: tx.exchange,
            currency: resolveQuoteCurrency(tx),
            costCurrency: resolveCostCurrency(tx),
            totalBuysQty: 0,
            totalBuysAmount: 0,
            totalSellsQty: 0,
            totalSellsAmount: 0,
            dividendsTotal: 0,
            lastTradeDate: undefined,
            quantity: 0,
            costBasis: 0,
            costBasisNative: 0,
        };

        const txDate = toIsoDateOnly(tx.date);
        if (txDate && (!entry.lastTradeDate || txDate > entry.lastTradeDate)) {
            entry.lastTradeDate = txDate;
        }

        if (action === 'DIVIDEND') {
            entry.dividendsTotal += amount;
            map.set(holdingId, entry);
            return;
        }

        if (!isSell) {
            entry.quantity += Math.abs(quantity);
            entry.costBasis += amount;
            entry.costBasisNative += nativeAmount;
            entry.totalBuysQty += Math.abs(quantity);
            entry.totalBuysAmount += amount;
        } else {
            const qtySold = Math.abs(quantity);
            const prevQty = Math.max(entry.quantity, 0);
            const avgCostBeforeSell = prevQty > 0 ? entry.costBasis / prevQty : 0;
            const avgNativeCostBeforeSell = prevQty > 0 ? entry.costBasisNative / prevQty : 0;
            entry.quantity = Math.max(0, prevQty - qtySold);
            entry.costBasis = Math.max(0, entry.costBasis - avgCostBeforeSell * Math.min(qtySold, prevQty));
            entry.costBasisNative = Math.max(0, entry.costBasisNative - avgNativeCostBeforeSell * Math.min(qtySold, prevQty));
            entry.totalSellsQty += qtySold;
            entry.totalSellsAmount += amount;
        }

        map.set(holdingId, entry);
    });

    return Array.from(map.entries())
        .filter(([, value]) => value.quantity > 0)
        .map(([holdingId, value]) => ({
            ...value,
            holdingId,
            avgCost: value.quantity > 0 ? value.costBasis / value.quantity : 0,
            avgCostNative: value.quantity > 0 ? value.costBasisNative / value.quantity : 0,
        }));
};
